import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  /** Shown when nothing is selected (also the first, clearable row). */
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Single-select dropdown with a type-to-filter search box.
 * Closes on outside click or Escape; keyboard up/down/enter to navigate.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "All",
  className,
  disabled,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [panelRect, setPanelRect] = useState<{ left: number; top: number; width: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? placeholder;

  // Prepend a clearable "placeholder" row so the user can reset to "all".
  const rows = useMemo<SearchableSelectOption[]>(() => {
    const all = [{ value: "", label: placeholder }, ...options];
    const q = query.trim().toLowerCase();
    return q ? all.filter((o) => o.label.toLowerCase().includes(q)) : all;
  }, [options, query, placeholder]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // The panel renders in a portal (so it escapes clipping ancestors); keep it
  // anchored to the trigger button on open, scroll, and resize.
  //
  // The app scales its whole UI via `zoom` on <html> (see --app-zoom in
  // index.css). getBoundingClientRect() returns zoom-scaled screen coords, but
  // the portaled panel lives inside that same zoomed root, so a fixed top/left
  // gets scaled by the zoom a second time — landing it high and to the left.
  // Divide the rect back out by the active zoom so the round-trip is exact.
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const r = buttonRef.current?.getBoundingClientRect();
      if (!r) return;
      const cs = getComputedStyle(document.documentElement);
      const zoom = parseFloat(cs.zoom) || parseFloat(cs.getPropertyValue("--app-zoom")) || 1;
      setPanelRect({ left: r.left / zoom, top: (r.bottom + 4) / zoom, width: r.width / zoom });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      // Focus the search box once the panel is mounted.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const select = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (rows[activeIdx]) select(rows[activeIdx].value);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-lg border border-border/70 bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 disabled:opacity-40",
          !value && "text-muted-foreground",
          className,
        )}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown className="ml-1 h-4 w-4 shrink-0 text-muted-foreground/70" />
      </button>

      {open && panelRect && createPortal(
        <div
          ref={panelRef}
          style={{ position: "fixed", left: panelRect.left, top: panelRect.top, width: panelRect.width }}
          className="z-50 overflow-hidden rounded-lg border border-border/70 bg-popover shadow-lg"
        >
          <div className="flex items-center gap-2 border-b border-border/60 px-2.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Search…"
              className="h-9 w-full bg-transparent text-sm focus:outline-none"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {rows.length === 0 && (
              <li className="px-3 py-2 text-sm text-muted-foreground">No matches</li>
            )}
            {rows.map((o, idx) => (
              <li key={o.value || "__all__"}>
                <button
                  type="button"
                  onClick={() => select(o.value)}
                  onMouseEnter={() => setActiveIdx(idx)}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm transition-colors",
                    idx === activeIdx ? "bg-primary text-primary-foreground" : "hover:bg-accent",
                    !o.value && idx !== activeIdx && "text-muted-foreground",
                  )}
                >
                  <span className="truncate">{o.label}</span>
                  {o.value === value && o.value !== "" && (
                    <Check className={cn("h-3.5 w-3.5 shrink-0", idx === activeIdx ? "text-primary-foreground" : "text-primary")} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
